import React from 'react';

export enum SortDirection {
  Ascending = 'ascending',
  Descending = 'descending',
}

export interface SortConfig<Fields> {
  key: keyof Fields;
  direction: SortDirection;
}

export const useSortableData = <Item,>(items: Item[]) => {
  const [sortConfig, setSortConfig] = React.useState<SortConfig<Item>>();

  const sortedItems = React.useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig) {
      const nullableData = sortableItems.filter(
        (el) => el[sortConfig.key] == null || el[sortConfig.key] === ''
      );
      const notNullableData = sortableItems.filter(
        (el) => el[sortConfig.key] != null && el[sortConfig.key] !== ''
      );

      notNullableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
      // null («никогда») всегда в конце — при «сначала свежие» и при «сначала старые»
      sortableItems = [...notNullableData, ...nullableData];
    }
    return sortableItems;
  }, [items, sortConfig]);

  const toggleSort = (
    key: SortConfig<Item>['key'],
    opts?: { preferDescending?: boolean }
  ) => {
    const first = opts?.preferDescending
      ? SortDirection.Descending
      : SortDirection.Ascending;
    const second =
      first === SortDirection.Descending
        ? SortDirection.Ascending
        : SortDirection.Descending;

    if (sortConfig?.key !== key) {
      setSortConfig({ key, direction: first });
      return;
    }
    if (sortConfig.direction === first) {
      setSortConfig({ key, direction: second });
      return;
    }
    setSortConfig(undefined);
  };

  const setSort = (
    key: SortConfig<Item>['key'],
    direction: SortDirection = SortDirection.Descending
  ) => {
    setSortConfig({ key, direction });
  };

  return {
    items: sortedItems,
    toggleSort,
    setSort,
    sortConfig,
    clearSort: () => setSortConfig(undefined),
  };
};
